// src/stores/MicrosoftStore.js - Zustand Store for Unified Microsoft Integration
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useMicrosoftStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Connection status
    microsoftConnection: null,
    microsoftConnectionLoading: false,

    // OAuth flow state (handles redirect to Microsoft)
    oauthUrl: null,
    oauthInitiating: false,

    // Disconnection
    disconnecting: false,

    // Connection state
    isConnected: false,

    // Cache state
    cache: {
      microsoftConnection: null,
      lastFetch: null
    },

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS (NO RETURN VALUES)
    // ================================

    /**
     * Load Microsoft connection status - NO RETURN VALUE
     */
    loadMicrosoftConnectionStatus: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ MicrosoftStore: Socket service not initialized');
        return;
      }

      console.log('🔌 MicrosoftStore: Loading connection status');
      set({
        microsoftConnectionLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_MICROSOFT_CONNECTION_STATUS, {});
    },

    /**
     * Start OAuth flow - generates OAuth URL
     * Frontend will redirect user to Microsoft
     */
    startMicrosoftOAuth: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ MicrosoftStore: Socket service not initialized');
        return;
      }

      console.log('🚀 MicrosoftStore: Starting OAuth flow');
      set({
        oauthInitiating: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.START_MICROSOFT_OAUTH, {});
    },

    /**
     * Disconnect Microsoft account and disable ALL features
     */
    disconnectMicrosoft: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ MicrosoftStore: Socket service not initialized');
        return;
      }

      console.log('🔌 MicrosoftStore: Disconnecting Microsoft');
      set({
        disconnecting: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.DISCONNECT_MICROSOFT, {});
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Check if Microsoft account is connected
     */
    isMicrosoftConnected: () => {
      const { microsoftConnection } = get();
      return microsoftConnection?.has_connection === true;
    },

    /**
     * Get connected account email
     */
    getAccountEmail: () => {
      const { microsoftConnection } = get();
      return microsoftConnection?.account_email || null;
    },

    /**
     * Check if Outlook Calendar is enabled
     */
    isOutlookEnabled: () => {
      const { microsoftConnection } = get();
      return microsoftConnection?.features?.outlook?.enabled === true;
    },

    /**
     * Check if Microsoft Teams is enabled
     */
    isTeamsEnabled: () => {
      const { microsoftConnection } = get();
      return microsoftConnection?.features?.teams?.enabled === true;
    },

    /**
     * Check if token is expired
     */
    isTokenExpired: () => {
      const { microsoftConnection } = get();
      return microsoftConnection?.is_expired === true;
    },

    /**
     * Get granted scopes
     */
    getGrantedScopes: () => {
      const { microsoftConnection } = get();
      return microsoftConnection?.granted_scopes || '';
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setMicrosoftConnectionLoaded: (data) => {
      console.log('📡 MicrosoftStore: Connection status received');

      set(state => ({
        microsoftConnection: data,
        microsoftConnectionLoading: false,
        isConnected: data?.has_connection === true,
        cache: {
          ...state.cache,
          microsoftConnection: data,
          lastFetch: new Date()
        }
      }));
    },

    _setOAuthUrlGenerated: (data) => {
      console.log('📡 MicrosoftStore: OAuth URL generated');

      set({
        oauthUrl: data.url,
        oauthInitiating: false
      });
    },

    _setMicrosoftDisconnected: (data) => {
      console.log('📡 MicrosoftStore: Microsoft disconnected');

      set({
        microsoftConnection: null,
        disconnecting: false,
        isConnected: false,
        cache: {
          microsoftConnection: null,
          lastFetch: null
        }
      });
    },

    // ================================
    // SOCKET CONNECTION MANAGEMENT (following CustomerStore pattern)
    // ================================

    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },

    _getSocketManager: () => {
      // Access socket manager from window global (following pattern)
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },

    _handleAuthReset: () => {
      console.log('🧹 MicrosoftStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        microsoftConnection: null,
        microsoftConnectionLoading: false,
        oauthUrl: null,
        oauthInitiating: false,
        disconnecting: false,
        isConnected: false,
        cache: {
          microsoftConnection: null,
          lastFetch: null
        },
        _initialized: false
      });
    },

    // ================================
    // INITIALIZATION (following CustomerStore pattern)
    // ================================

    _initialize: (socketManager) => {
      const state = get();

      console.log('🔌 MicrosoftStore: Initializing with socket manager');

      // Store socket manager reference
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load Microsoft connection status if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('🔄 MicrosoftStore: Auto-loading Microsoft connection status (first time)');
        state.loadMicrosoftConnectionStatus();
      }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER INITIALIZATION
// ================================

/**
 * Initialize socket event listeners for MicrosoftStore
 * Called by SocketContext on auth success
 */
export const initializeMicrosoftStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    console.error('❌ MicrosoftStore: Cannot initialize - socketManager not available');
    return;
  }

  console.log('🎧 MicrosoftStore: Initializing socket listeners');

  const store = useMicrosoftStore.getState();

  // Mark as initialized
  useMicrosoftStore.setState({ _initialized: true });

  // Central event handler
  const handleSocketEvent = (eventName, responseData) => {
    const store = useMicrosoftStore.getState();

    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.MICROSOFT_CONNECTION_STATUS_LOADED:
        store._setMicrosoftConnectionLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.MICROSOFT_OAUTH_URL_GENERATED:
        store._setOAuthUrlGenerated(responseData.data);
        break;

      case SOCKET_EVENTS.MICROSOFT_DISCONNECTED:
        store._setMicrosoftDisconnected(responseData.data);
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_MICROSOFT_CONNECTION_STATUS_FAILED:
        useMicrosoftStore.setState({ microsoftConnectionLoading: false });
        break;

      case SOCKET_EVENTS.START_MICROSOFT_OAUTH_FAILED:
        useMicrosoftStore.setState({ oauthInitiating: false });
        break;

      case SOCKET_EVENTS.DISCONNECT_MICROSOFT_FAILED:
        useMicrosoftStore.setState({ disconnecting: false });
        console.log('❌ MicrosoftStore: Disconnect failed -', responseData.message);
        break;

      default:
        console.warn('⚠️ MicrosoftStore: Unhandled event:', eventName);
    }
  };

  // Set up individual event listeners
  const microsoftEvents = [
    // Success events
    SOCKET_EVENTS.MICROSOFT_CONNECTION_STATUS_LOADED,
    SOCKET_EVENTS.MICROSOFT_OAUTH_URL_GENERATED,
    SOCKET_EVENTS.MICROSOFT_DISCONNECTED,
    // Error events
    SOCKET_EVENTS.GET_MICROSOFT_CONNECTION_STATUS_FAILED,
    SOCKET_EVENTS.START_MICROSOFT_OAUTH_FAILED,
    SOCKET_EVENTS.DISCONNECT_MICROSOFT_FAILED
  ];

  microsoftEvents.forEach(eventName => {
    socketManager.universalHandler.on(eventName, (data) => {
      handleSocketEvent(eventName, { event: eventName, data });
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useMicrosoftStore.getState()._handleAuthReset();
    });
  }

  // Initialize the store
  useMicrosoftStore.getState()._initialize(socketManager);

  console.log('✅ MicrosoftStore: Socket listeners initialized');
};

export default useMicrosoftStore;
